import type { ReactNode } from 'react';

import DetailHeader from './DetailHeader';
import DetailHeaderContactPure from './DetailHeaderContactPure';

export type ProspectDetailPureProps = {
  /** Person's display name. */
  name: string;
  linkedinUrl?: string;
  /** Title / role line under the name. */
  subtitle?: string;
  orgName?: string;
  /** Click the org line (e.g. scroll to / open the firm). Omit to render it as plain text. */
  onClickOrg?: () => void;
  /** Whether the viewer is already connected to them on LinkedIn. */
  isConnected?: boolean;
  /** Small inline chips next to the name (warm path, event tags…). */
  nameChips?: ReactNode;
  /** The header's action row — only actions that work without a contact (no stage, notes, tasks). */
  actions?: ReactNode;
  /** Muted one-liner under the actions, e.g. "Not in your contacts yet". */
  note?: ReactNode;
  /** Closes the sidebar; also wires Escape through the shared esc stack (DetailHeader). */
  onClose?: () => void;
  /** Sidebar body sections. */
  children?: ReactNode;
  'data-testid'?: string;
};

/**
 * The standard contact sidebar's chrome for someone who is NOT a contact yet — same header
 * (`DetailHeader` + `DetailHeaderContactPure`), same proportions and close/Escape behaviour as
 * `ContactDetailPageCore`, but no contact-only controls (stage, notes, tasks, campaigns-in-flight),
 * because those would be dead buttons until the person exists. Data-free: hosts pass everything.
 */
export function ProspectDetailPure({
  name,
  linkedinUrl,
  subtitle,
  orgName,
  onClickOrg,
  isConnected,
  nameChips,
  actions,
  note,
  onClose,
  children,
  'data-testid': testId,
}: ProspectDetailPureProps) {
  return (
    <div className="flex h-full flex-col overflow-hidden" data-testid={testId ?? 'prospect-detail'}>
      <DetailHeader
        className="bg-slate-50 dark:bg-slate-900"
        onClose={onClose}
        footer={
          actions != null || note != null ? (
            <div className="flex flex-col gap-1.5 px-4 pt-1 pb-3">
              {actions != null && <div className="flex flex-wrap items-center justify-end gap-2">{actions}</div>}
              {note != null && <div className="text-right text-xs text-gray-500 dark:text-gray-400">{note}</div>}
            </div>
          ) : undefined
        }>
        <DetailHeaderContactPure
          name={name}
          avatarUrls={[]}
          linkedinUrl={linkedinUrl}
          subtitle={subtitle}
          orgName={orgName}
          onClickOrg={onClickOrg}
          isConnected={isConnected}>
          {nameChips != null && <div className="mt-1 flex flex-wrap items-center gap-1">{nameChips}</div>}
        </DetailHeaderContactPure>
      </DetailHeader>
      <div className="min-h-0 flex-1 overflow-auto">{children}</div>
    </div>
  );
}
